// Mulberry32: tiny, fast, deterministic 32-bit RNG.
// The same seed produces the same stream on every platform — required for
// client/server replay parity and for "everyone in the world plays the same
// course today".
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function next(): number {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// FNV-1a 32-bit hash: stable, no allocations, matches on all runtimes.
export function seedFromString(str: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

// Helper: yield `count` independent daily-stream seeds derived from one parent.
export function splitSeed(parent: number, count: number): number[] {
  const rng = mulberry32(parent);
  const out = new Array<number>(count);
  for (let i = 0; i < count; i++) {
    out[i] = Math.floor(rng() * 0xffffffff) >>> 0;
  }
  return out;
}

// Map a float in [0,1) to [lo,hi).
export function lerp(lo: number, hi: number, t: number): number {
  return lo + (hi - lo) * t;
}

export function randRange(rng: () => number, lo: number, hi: number): number {
  return lerp(lo, hi, rng());
}

export function randInt(rng: () => number, lo: number, hi: number): number {
  return Math.floor(lerp(lo, hi + 1, rng()));
}

export function pick<T>(rng: () => number, arr: readonly T[]): T {
  return arr[Math.floor(rng() * arr.length)]!;
}
